import { useCallback, useMemo } from 'react';

import { useBreakPoints, useFormListValidation, useFormListValidator } from 'core';
import { ClickableOptionsCard } from 'core/Components';
import { centsToDollar } from 'core/Utils';
import {
  Subscription,
  useShowDonationStoreItem,
  useShowUserSubscription,
  useUpdateUserSubscription,
} from 'store/Api';
import { DonationAmountForm } from 'store/Components';
import {
  ButtonMantine,
  Card,
  FullWidthButtonGroup,
  Group,
  ImageMantine,
  Modal,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';
import { UseFormReturnType, useForm } from 'ui/Form';
import { AmexLogo, MastercardLogo, VisaLogo } from 'ui/Icons';

import {
  ModalTitleWithBackButton,
  useDonationSettingModalProvider,
} from '../DonationSettingsModalProvider';

type EditRecurringDonationForm = {
  pricing: {
    value?: number;
    label?: string;
    maxAmount?: number;
  };
};
type EditRecurringDonationForms = {
  recurring_donation: UseFormReturnType<EditRecurringDonationForm>;
};

const CREDIT_CARD_MAP = {
  amex: AmexLogo,
  visa: VisaLogo,
  mastercard: MastercardLogo,
};
const EditRecurringDonationFooter = ({
  subscriptionId,
  onSuccess,
  openCancel,
}: {
  subscriptionId?: string;
  onSuccess: () => void;
  openCancel: () => void;
}) => {
  const { useGetForm, validateForms } = useFormListValidation<EditRecurringDonationForms>();
  const { mutate: updateSubscription, isPending: isLoading } = useUpdateUserSubscription();

  const form = useGetForm('recurring_donation');

  const handleUpdateSubscription = () => {
    if (validateForms().isValid) {
      updateSubscription(
        {
          subscription_uuid: subscriptionId ?? '',
          subscription: {
            price: Number(form.values.pricing.value),
            designation: form.values.pricing.label,
          },
        },
        {
          onSuccess() {
            notifications.show({
              message: 'Donation Amount Updated',
              variant: 'success',
            });
            onSuccess();
          },
          onError() {
            notifications.show({
              message: 'Error updating donation amount',
              variant: 'error',
            });
          },
        }
      );
    }
  };

  return (
    <FullWidthButtonGroup>
      <ButtonMantine color="cerise" onClick={openCancel}>
        Cancel Donation
      </ButtonMantine>
      <ButtonMantine onClick={handleUpdateSubscription} loading={isLoading}>
        Save Changes
      </ButtonMantine>
    </FullWidthButtonGroup>
  );
};

const PaymentMethod = ({ subscription, onClick }: { subscription?: Subscription; onClick }) => {
  const { isSmallScreen } = useBreakPoints();
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={4}>Payment Method</Title>
        <ButtonMantine size={isSmallScreen ? 'xs' : 'sm'} variant="subtle" onClick={onClick}>
          Update Payment Method
        </ButtonMantine>
      </Group>
      <Card withBorder p="sm" bg="gray.0">
        <Group justify="space-between" mih="40px" wrap="nowrap">
          <Group wrap="nowrap">
            <ImageMantine
              fit="contain"
              w={62}
              h="auto"
              src={CREDIT_CARD_MAP[subscription?.metadata.brand ?? 'visa']}
            />
            <Group>
              <Text fz={14} mr="xs">{`•••• •••• •••• ${
                subscription?.metadata.last4 ?? '••••'
              }`}</Text>
            </Group>
          </Group>
        </Group>
      </Card>
    </Stack>
  );
};

const DonationAmountButtonGroup = ({ subscription }: { subscription?: Subscription }) => {
  const { data: storeItem } = useShowDonationStoreItem({
    causeId: subscription?.cause_id,
  });

  const form = useForm({
    initialValues: {
      pricing: {},
      label: '',
    },
  });

  useFormListValidator({
    form,
    id: 'recurring_donation',
  });

  const handleAmountChange = useCallback(
    ({ value, label, maxAmount }: { value: number; label?: string; maxAmount?: number }) => {
      form.setFieldValue('pricing', { value, label, maxAmount });
    },
    [form]
  );

  const formPrice = useMemo(() => storeItem?.default_pricing ?? [], [storeItem?.default_pricing]);

  return (
    <Stack>
      <DonationAmountForm onChange={handleAmountChange} pricing={formPrice} isPledge={false} />
    </Stack>
  );
};

const EditRecurringDonationModalContent = ({
  subscriptionId,
  onClick,
  onSuccess,
  openCancel,
}: {
  subscriptionId?: string;
  onClick: () => void;
  onSuccess: () => void;
  openCancel: () => void;
}) => {
  const { data: subscriptionData } = useShowUserSubscription(subscriptionId);

  return (
    <Stack gap="lg">
      <ClickableOptionsCard>
        <ClickableOptionsCard.Row
          title={subscriptionData?.cause.metadata?.name}
          subtitle={`${centsToDollar(subscriptionData?.price)} / month`}
          rightSection={<></>}
        />
      </ClickableOptionsCard>
      <DonationAmountButtonGroup subscription={subscriptionData} />
      <PaymentMethod subscription={subscriptionData} onClick={onClick} />
      <EditRecurringDonationFooter
        subscriptionId={subscriptionId}
        onSuccess={onSuccess}
        openCancel={openCancel}
      />
    </Stack>
  );
};

const EditRecurringDonationModal = () => {
  const { opened, close, meta, openModalById } = useDonationSettingModalProvider({
    id: 'EditRecurring',
  });
  const { subscriptionId } = meta;
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Edit Recurring Donation</ModalTitleWithBackButton>}
    >
      <EditRecurringDonationModalContent
        subscriptionId={subscriptionId}
        onClick={() => openModalById('UpdatePayment')}
        onSuccess={() => openModalById('ManageRecurring')}
        openCancel={() => openModalById('CancelDonation')}
      />
    </Modal>
  );
};

export { EditRecurringDonationModal, EditRecurringDonationModalContent };
